import React, { useState, useEffect, useRef } from "react";
import LoginPage from "./components/pages/LoginPage";
import GamePage from "./components/pages/GamePage";
import ReconnectDialog from "./components/ui/ReconnectDialog";
import { useSocketConnection } from "./hooks/useSocketConnection";
import { getCachedPlayerName, cachePlayerName, getCachedRoomName, cacheRoomName, getActiveGameSession, clearActiveGameSession } from "./utils/localStorageUtils";

const App = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [name, setName] = useState(getCachedPlayerName());
  
  const getInitialRoom = () => {
    const urlParams = new URLSearchParams(window.location.search);  
    const roomParam = urlParams.get('room');
    if (roomParam) {
      return roomParam;
    }
    // Check for cached room name if no URL param
    const cachedRoom = getCachedRoomName();
    if (cachedRoom) {
      return cachedRoom;
    }
    return process.env.NODE_ENV === 'development' ? "test" : "";
  };
  
  const [room, setRoom] = useState(getInitialRoom());
  const socket = useSocketConnection();
  const autoReconnectAttemptedRef = useRef(false);
  const [showReconnectDialog, setShowReconnectDialog] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);

  // Cache name to localStorage whenever it changes
  useEffect(() => {
    cachePlayerName(name);
  }, [name]);

  // Cache room name to localStorage whenever it changes
  useEffect(() => {
    if (room && room.trim()) {
      cacheRoomName(room);
    }
  }, [room]);

  // Check for active game session and verify with server before showing reconnect dialog
  useEffect(() => {
    // Only check once when app loads
    if (autoReconnectAttemptedRef.current || loggedIn || showReconnectDialog) return;
    
    const activeSession = getActiveGameSession();
    
    if (activeSession) {
      const { room: savedRoom, name: savedName } = activeSession;
      
      // Check if we have socket connection
      if (socket.current && socket.current.connected) {
        console.log('[AUTO-RECONNECT] Found active session, verifying with server:', { savedRoom, savedName });
        
        // Set timeout in case server doesn't respond
        let responded = false;
        const timeout = setTimeout(() => {
          if (!responded) {
            responded = true;
            console.log('[AUTO-RECONNECT] Server check timeout, clearing session');
            clearActiveGameSession();
            autoReconnectAttemptedRef.current = true;
          }
        }, 5000); // 5 second timeout
        
        // Verify game exists and can be rejoined before showing dialog
        socket.current.emit('check_game_exists', { room: savedRoom, name: savedName }, (response) => {
          if (responded) return; // Already handled by timeout
          responded = true;
          clearTimeout(timeout);
          
          if (response && response.exists && response.canRejoin) {
            console.log('[AUTO-RECONNECT] Game exists and can be rejoined, showing dialog');
            setPendingSession(activeSession);
            setShowReconnectDialog(true);
            autoReconnectAttemptedRef.current = true;
          } else {
            console.log('[AUTO-RECONNECT] Cannot rejoin game:', response?.reason || 'Unknown reason');
            // Game doesn't exist or can't be rejoined, clear the session
            clearActiveGameSession();
            autoReconnectAttemptedRef.current = true;
          }
        });
      } else if (socket.current) {
        // Socket exists but not connected yet, wait for connection
        let connectionCheckInterval = null;
        let connectionTimeout = null;
        
        connectionCheckInterval = setInterval(() => {
          if (socket.current && socket.current.connected) {
            clearInterval(connectionCheckInterval);
            if (connectionTimeout) clearTimeout(connectionTimeout);
            
            console.log('[AUTO-RECONNECT] Socket connected, verifying with server:', { savedRoom, savedName });
            
            // Set timeout for server response
            let responded = false;
            const timeout = setTimeout(() => {
              if (!responded) {
                responded = true;
                console.log('[AUTO-RECONNECT] Server check timeout, clearing session');
                clearActiveGameSession();
                autoReconnectAttemptedRef.current = true;
              }
            }, 5000);
            
            // Retry the check
            socket.current.emit('check_game_exists', { room: savedRoom, name: savedName }, (response) => {
              if (responded) return;
              responded = true;
              clearTimeout(timeout);
              
              if (response && response.exists && response.canRejoin) {
                console.log('[AUTO-RECONNECT] Game exists and can be rejoined, showing dialog');
                setPendingSession(activeSession);
                setShowReconnectDialog(true);
                autoReconnectAttemptedRef.current = true;
              } else {
                console.log('[AUTO-RECONNECT] Cannot rejoin game:', response?.reason || 'Unknown reason');
                clearActiveGameSession();
                autoReconnectAttemptedRef.current = true;
              }
            });
          }
        }, 500);
        
        // Cleanup interval after 10 seconds
        connectionTimeout = setTimeout(() => {
          if (connectionCheckInterval) {
            clearInterval(connectionCheckInterval);
            console.log('[AUTO-RECONNECT] Connection wait timeout, clearing session');
            clearActiveGameSession();
            autoReconnectAttemptedRef.current = true;
          }
        }, 10000);
      }
    }
  }, [socket, loggedIn, showReconnectDialog]);

  // Handle rejoin action
  const handleRejoin = () => {
    if (!pendingSession) return;
    
    const { room: savedRoom, name: savedName } = pendingSession;
    
    console.log('[AUTO-RECONNECT] User chose to rejoin, attempting to rejoin:', { savedRoom, savedName });
    
    // Set the name and room
    setName(savedName);
    setRoom(savedRoom);
    
    // Close dialog
    setShowReconnectDialog(false);
    
    // Wait a bit for socket to be ready
    setTimeout(() => {
      if (socket.current && socket.current.connected) {
        console.log('[AUTO-RECONNECT] Attempting to rejoin room');
        socket.current.emit('join_room', { 
          room: savedRoom, 
          name: savedName 
        });
        
        // Listen for successful join or error
        const handleJoinSuccess = () => {
          console.log('[AUTO-RECONNECT] Successfully rejoined game');
          setLoggedIn(true);
          socket.current.off('join_room_error', handleJoinError);
        };
        
        const handleJoinError = (error) => {
          console.log('[AUTO-RECONNECT] Failed to rejoin:', error);
          clearActiveGameSession();
          setPendingSession(null);
          socket.current.off('player_count', handleJoinSuccess);
          
          // Show user-friendly error message
          const errorMessage = typeof error === 'string' ? error : 'The game may have ended or you are no longer able to rejoin.';
          alert(`❌ Cannot rejoin game\n\n${errorMessage}\n\nYou will be returned to the login page.`);
          
          // Return to login page
          setLoggedIn(false);
        };
        
        // Listen for either success (player_count event) or error
        socket.current.once('player_count', handleJoinSuccess);
        socket.current.once('join_room_error', handleJoinError);
      }
    }, 1000); // Give socket time to connect
  };

  // Handle leave action - properly quit the game on server
  const handleLeave = () => {
    if (!pendingSession) return;
    
    const { room: savedRoom, name: savedName } = pendingSession;
    
    console.log('[AUTO-RECONNECT] User chose to leave game, quitting on server:', { savedRoom, savedName });
    
    // Close dialog immediately
    setShowReconnectDialog(false);
    
    // Function to quit the game
    const quitGame = () => {
      if (!socket.current || !socket.current.connected) {
        console.log('[AUTO-RECONNECT] Socket not connected, clearing session only');
        clearActiveGameSession();
        setPendingSession(null);
        return;
      }
      
      // First join the room (this will work even if game started, since player is in disconnectedPlayers)
      // We need to join first so socket.nickname is set for the end_game handler
      socket.current.emit('join_room', { room: savedRoom, name: savedName });
      
      // Listen for successful join (player_count event indicates successful join)
      const handleJoinSuccess = () => {
        console.log('[AUTO-RECONNECT] Successfully joined room, now quitting game');
        socket.current.off('player_count', handleJoinSuccess);
        socket.current.off('join_room_error', handleJoinError);
        
        // Wait a moment for join to fully complete, then quit
        setTimeout(() => {
          if (socket.current && socket.current.connected) {
            console.log('[AUTO-RECONNECT] Emitting end_game to quit');
            socket.current.emit('end_game', savedRoom);
            
            // Listen for quit confirmation
            const handleQuitConfirm = () => {
              console.log('[AUTO-RECONNECT] Quit confirmed');
              socket.current.off('player_quit', handleQuitConfirm);
              clearActiveGameSession();
              setPendingSession(null);
            };
            
            socket.current.once('player_quit', handleQuitConfirm);
            
            // Clear session after timeout even if no confirmation
            setTimeout(() => {
              clearActiveGameSession();
              setPendingSession(null);
            }, 2000);
          } else {
            clearActiveGameSession();
            setPendingSession(null);
          }
        }, 300);
      };
      
      const handleJoinError = (error) => {
        console.log('[AUTO-RECONNECT] Could not join room to quit:', error);
        socket.current.off('player_count', handleJoinSuccess);
        socket.current.off('join_room_error', handleJoinError);
        // Still clear session even if join fails
        clearActiveGameSession();
        setPendingSession(null);
      };
      
      socket.current.once('player_count', handleJoinSuccess);
      socket.current.once('join_room_error', handleJoinError);
    };
    
    // Wait for socket connection if needed
    if (socket.current && socket.current.connected) {
      quitGame();
    } else if (socket.current) {
      // Wait for connection
      const checkConnection = setInterval(() => {
        if (socket.current && socket.current.connected) {
          clearInterval(checkConnection);
          quitGame();
        }
      }, 500);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkConnection);
        console.log('[AUTO-RECONNECT] Socket connection timeout, clearing session');
        clearActiveGameSession();
        setPendingSession(null);
      }, 5000);
    } else {
      // No socket, just clear session
      clearActiveGameSession();
      setPendingSession(null);
    }
  };

  // Auto-login in dev mode
  useEffect(() => {
    const isDev = process.env.NODE_ENV === 'development';
    
    if (isDev && socket.current && !loggedIn && false) {
      // Generate satirical investment/market/economy themed random name
      const firstParts = ['Pump', 'Dump', 'HODL', 'Moon', 'Diamond', 'Paper', 'YOLO', 'FOMO', 'Shill', 'Whale', 'Bag', 'Rug', 'Lambo', 'Tendies', 'Stonks', 'Bear', 'Bull', 'Crypto', 'Day', 'Swing', 'Value', 'Momentum', 'Arbitrage', 'Insider', 'Flash', 'Bubble', 'Recession', 'Inflation', 'Quant', 'Algo', 'Hedge', 'VC', 'IPO', 'SPAC', 'Meme', 'Blue', 'Penny', 'Dividend', 'Growth', 'Margin', 'Short', 'Gamma', 'Dead', 'Golden'];
      const secondParts = ['Trader', 'Investor', 'Bro', 'Fund', 'Stock', 'Chip', 'Call', 'Squeeze', 'Cat', 'Cross', 'Hands', 'Pull', 'Maker', 'Crash', 'Bot', 'Street', 'Index', 'ETF', 'Holder', 'Hodler', 'Shark', 'Wolf', 'Bull', 'Bear', 'Whale', 'Dolphin', 'Shrimp', 'Diamond', 'Paper', 'Iron', 'Steel', 'Silver', 'Gold', 'Platinum'];
      const randomName = `${firstParts[Math.floor(Math.random() * firstParts.length)]}${secondParts[Math.floor(Math.random() * secondParts.length)]}${Math.floor(Math.random() * 100)}`;
      
      console.log(`[DEV MODE] Auto-joining room "${room}" as "${randomName}"`);
      
      setName(randomName);
      socket.current.emit('join_room', { room, name: randomName });
      setLoggedIn(true);
    }
  }, [loggedIn, room, socket]);

  

  return (
    <div className="App flex-centered">
      {loggedIn ? (
        <GamePage
          room={room}
          socket={socket}
          name={name}
          setLoggedIn={setLoggedIn}
        />
      ) : (
        <LoginPage
          socket={socket}
          setLoggedIn={setLoggedIn}
          name={name}
          setName={setName}
          room={room}
          setRoom={setRoom}
        />
      )}

      {/* Reconnect Dialog */}
      {pendingSession && (
        <ReconnectDialog
          open={showReconnectDialog}
          room={pendingSession.room}
          playerName={pendingSession.name}
          onRejoin={handleRejoin}
          onLeave={handleLeave}
        />
      )}
    </div>
  );
};

export default App;
